import { LabelType, Options } from '@angular-slider/ngx-slider';
import { padStart } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  ViewContainerRef,
  ViewChild,
  ChangeDetectorRef,
  AfterViewInit,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';

import { ISnListQueryParameters } from '@lib/api';
import { SnCamerasAPI, SnSavedSearchAPI } from '@lib/api/services';
import { SnCurrentOrganization } from '@lib/auth';
import { SnFormFieldConfig } from '@lib/form';
import { t } from '@lib/translation';
import { SnDrawerRef, SnDrawerService, SN_DRAWER_DATA } from '@lib/ui/drawer';

import { CreateSentryDialogComponent } from '../create-sentry-dialog/create-sentry-dialog.component';
import { SentrySearchResultsDrawerComponent } from '../sentry-search-results-drawer/sentry-search-results-drawer.component';

@Component({
  selector: 'fs-sentry-search-form-drawer',
  templateUrl: './sentry-search-form-drawer.component.html',
  styleUrls: ['./sentry-search-form-drawer.component.scss'],
})
export class SentrySearchFormDrawerComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('timeSlider', { static: false })
  public timeSlider: any;

  public readonly isNew: boolean;

  public deconstructedProxyData: any;
  public cameras = [];
  public eventLabels: string[] = [];
  public colors: any[] = [];
  public vehicleTypes: any[] = [];
  public vehicleMakes: any[] = [];
  public detectionLabels: string[] = [];
  public form: UntypedFormGroup = new UntypedFormGroup({});
  public formEvents: UntypedFormGroup = new UntypedFormGroup({});
  public formModel: {
    name?: any;
    aiCameras?: any;
    eventDetection?: any;
    label?: any;
    colorTop?: any;
    colorBottom?: any;
    vehicleType?: any;
    carColors?: any;
    confidence?: any;
    colorConfidence?: any;
    is_male?: string;
    has_bag?: string;
    has_backpack?: string;
    has_hat?: string;
    startDate?: Date;
    noEnd?: any;
    endDate?: Date;
    isActiveAlert?: any;
  } = {};
  public formModelEvents: {
    aiCameras?: any;
    startDate?: Date;
    noEnd?: any;
    endDate?: Date;
  } = {};
  public startTime = 0;
  public endTime = 1440;
  public allowAi: boolean;

  public detectionMode: string = t('map.tools.createSentryDialog.form.detection');

  public isLoading = false;
  public isUpdatingFacets = false;
  public progressMessage: string;
  public sliderOptions: Options = {
    floor: 0,
    ceil: 1440,
    step: 60,
    translate: (value: number, label: LabelType): string => {
      const num = value;
      const hours = num / 60;
      const rhours = Math.floor(hours);
      const minutes = (hours - rhours) * 60;
      const rminutes = Math.round(minutes);
      return `${padStart(rhours.toString(), 2, '0')}:${padStart(rminutes.toString(), 2, '0')}`;
    },
  };
  public formFields: SnFormFieldConfig[];
  public formFieldsEvents: SnFormFieldConfig[];

  get params$(): Observable<any> {
    return this._params$.asObservable();
  }
  get colors$(): Observable<any[]> {
    return this._colors$.asObservable();
  }
  get vehicleTypes$(): Observable<any[]> {
    return this._vehicleTypes$.asObservable();
  }
  get detectionLabels$(): Observable<any[]> {
    return this._detectionLabels$.asObservable();
  }
  get eventLabels$(): Observable<any[]> {
    return this._eventLabels$.asObservable();
  }
  get eventRules$(): Observable<any[]> {
    return this._eventRules$.asObservable();
  }
  get colorTop$(): Observable<any[]> {
    return this._colorTop$.asObservable();
  }
  get colorBottom$(): Observable<any[]> {
    return this._colorBottom$.asObservable();
  }
  get vehicleMakes$(): Observable<any[]> {
    return this._vehicleMakes$.asObservable();
  }

  private readonly _params$ = new Subject<any>();
  private readonly _colors$ = new Subject<any[]>();
  private readonly _vehicleTypes$ = new Subject<any[]>();
  private readonly _detectionLabels$ = new Subject<any[]>();
  private readonly _eventLabels$ = new Subject<any[]>();
  private readonly _eventRules$ = new Subject<any[]>();
  private readonly _colorTop$ = new Subject<any[]>();
  private readonly _colorBottom$ = new Subject<any[]>();
  private readonly _destroy$ = new Subject<void>();
  private readonly _vehicleMakes$ = new Subject<any[]>();
  constructor(
    @Inject(SN_DRAWER_DATA) public data: any,
    private readonly _camerasAPI: SnCamerasAPI,
    private readonly _sentrySearchApi: SnSavedSearchAPI,
    private readonly _drawer: SnDrawerService,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _drawerRef: SnDrawerRef<SentrySearchFormDrawerComponent>,
    private readonly _toastrService: ToastrService,
    private readonly _matDialog: MatDialog,
    private readonly _viewContainerRef: ViewContainerRef,
    private readonly _cd: ChangeDetectorRef
  ) {
    this.allowAi = this._currentOrganization.plan.allowAi;
    this.isNew = !data.id;
  }

  public ngOnInit(): void {
    this._setForm();
    if (!this.isNew) {
      this.deconstructedProxyData = this.data.rule;
      this._patchForm(this.deconstructedProxyData);
    } else {
      const cameraIds = this.data.map((cam) => cam.id);
      const today = new Date();
      const yesterday = new Date();
      const tomorrow = new Date();
      yesterday.setDate(today.getDate() - 1);
      tomorrow.setDate(today.getDate() + 1);
      this.formModel = {
        label: '',
        ...this.formModel,
        aiCameras: cameraIds,
        startDate: yesterday,
        endDate: today,
      };
      this.formModelEvents = {
        ...this.formModelEvents,
        aiCameras: cameraIds,
        startDate: yesterday,
        endDate: today,
      };
    }
    this._fetchFacets({
      timestamp__gte: Math.floor(this.formModel.startDate?.getTime() / 1000),
      timestamp__lte: Math.ceil(this.formModel.endDate?.getTime() / 1000),
    });
  }

  public ngAfterViewInit(): void {
    this._updateFacetDates();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public tabChanged(tabChangeEvent: MatTabChangeEvent): void {
    tabChangeEvent.index === 1
    ? (this.detectionMode = t('map.tools.createSentryDialog.form.event'))
    : (this.detectionMode = t('map.tools.createSentryDialog.form.detection'));
  }

  public submitForm(formInput?: any): void {
    const formData = this.formModel;
    const mode = formData.eventDetection;
    const queryParams = this._formToParams(formData, mode);
    const isActiveAlert = !!formData.isActiveAlert;
    this._camerasAPI
      .getAiList(queryParams)
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        if (!this.isNew) {
          this._sentrySearchApi.setCameras(res.results);
          this._patchSavedSearch({
            name: formData.name,
            queryParams,
            isActiveAlert,
          });
        } else {
          this._saveSearch({
            name: formData.name,
            queryParams,
            isActiveAlert,
          });
        }
        this._cd.detectChanges();
      });
  }

  public resetForm(): void {
    this.formModel = {
      ...this.formModel,
      label: null,
      carColors: null,
      confidence: 50,
      colorConfidence: null,
      is_male: null,
      has_hat: null,
      has_backpack: null,
      has_bag: null,
      colorTop: null,
      colorBottom: null,
    };
  }

  public viewResults(): void {
    const formData = this.formModel;
    const mode = formData.eventDetection;
    const queryParams = this._formToParams(formData, mode);
    this._openResultsDrawer(queryParams);
  }

  public openCreateSentryDialog(): any {
    const dialog = this._matDialog.open(CreateSentryDialogComponent, {
      data: {
        ...this.formModel,
        timeSliderStart: this.timeSlider.value,
        timeSliderEnd: this.timeSlider.highValue,
      },
    });

    dialog
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((response) => {
        if (response !== 'close') {
          this._patchNewForm(response);
          this.submitForm();
        }
      });
  }

  private _openResultsDrawer(query: any): void {
    this._drawer.open(SentrySearchResultsDrawerComponent, {
      viewContainerRef: this._viewContainerRef,
      data: query,
      closeOnNavigation: true,
      hasBackdrop: false,
      width: 380,
      panelClass: ['hide-on-fullscreen'],
    });
  }

  private _saveSearch(data: any): void {
    this._sentrySearchApi
      .saveSentrySearch(data)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this._toastrService.success(t('sentry.search.saveSuccess'));
          this._drawerRef.close();
        },
        error: () => {
          this._toastrService.error(t('sentry.search.saveFailed'));
        },
      });
  }

  private _patchSavedSearch(data: any): void {
    this._sentrySearchApi
      .updateSearch(this._sentrySearchApi.search.id, data)
      .pipe(takeUntil(this._destroy$))
      .subscribe();
  }

  private _patchNewForm(data: any): void {
    this.formModel = {
      ...this.formModel,
      name: data.name,
      startDate: data.startDate,
      endDate: data.endDate,
      isActiveAlert: data.isActiveAlert,
    };
  }

  private _patchForm(data: any): void {
    switch (data.fields.label_In[0]) {
      case 'person':
        {
          this.formModel = {
            aiCameras: data.fields?.cameraId_In,
            label: data.fields?.label_In?.toString(),
            startDate: new Date(data.fields?.timestamp_Gte * 1000),
            endDate: new Date(data.fields?.timestamp_Lte * 1000),
            confidence: data.fields?.confidence_Gte,
            is_male:
              data.fields?.metadata_Attributes_IsMale_Confidence_Gte === 50 ? '>=50' : '< 50',
            has_hat:
              data.fields?.metadata_Attributes_HasHat_Confidence_Gte === 50 ? '>=50' : '< 50',
            has_backpack:
              data.fields?.metadata_Attributes_HasBackpack_Confidence_Gte === 50 ? '>=50' : '< 50',
            has_bag:
              data.fields?.metadata_Attributes_HasBag_Confidence_Gte === 50 ? '>=50' : '< 50',
            colorTop: data.fields?.metadata_ColorTop_In,
            colorBottom: data.fields?.metadata_ColorBottom_In,
          };
          this.startTime = parseFloat(data.fields?.timestamp_Hour_Gte_Offset.split(',')[0]) * 60;
          this.endTime = parseFloat(data.fields?.timestamp_Hour_Lt_Offset.split(',')[0]) * 60;
        }
        break;
      case 'car': {
        this.formModel = {
          aiCameras: data.fields?.cameraId_In,
          label: data.fields?.label_In?.toString(),
          carColors: data.fields?.metadata_CarColor_In
            ? data.fields?.metadata_CarColor_In[0]
            : null,
          vehicleType: data.fields?.metadata_VehicleType_In
            ? data.fields?.metadata_VehicleType_In[0].replace('/', ' / ') ||
              data.fields?.metadata_VehicleType_In[0]
            : null,
          startDate: new Date(data.fields?.timestamp_Gte * 1000),
          endDate: new Date(data.fields?.timestamp_Lte * 1000),
          confidence: data.fields?.confidence_Gte,
          colorConfidence: data.fields?.metadata_CarColor_Confidence_Gte,
        };
        this.startTime = parseFloat(data.fields?.timestamp_Hour_Gte_Offset.split(',')[0]) * 60;
        this.endTime = parseFloat(data.fields?.timestamp_Hour_Lt_Offset.split(',')[0]) * 60;
      }
    }
  }

  private _formToParams(formData: any, mode: string): any {
    const cameras = formData.aiCameras;
    const startDate = new Date(formData.startDate);
    const date = new Date();
    const offset = (date.getTimezoneOffset() * -1) / 60;
    const startTime = `${this.timeSlider.value / 60}, ${offset}`;
    const endTime = `${this.timeSlider.highValue / 60}, ${offset}`;
    const endDate = new Date(formData.endDate);
    const eventRule = formData.eventRule;

    let params: any;

    if (mode === 'detection') {
      params = {
        camera_id__in: cameras?.join(','),
        label__in: formData.label || null,
        timestamp__gte: Math.floor(startDate?.getTime() / 1000),
        timestamp__hour__gte__offset: startTime,
        timestamp__hour__lt__offset: endTime,
        confidence__gte: formData.confidence,
      };
    }

    if (mode === 'event') {
      params = {
        camera_id__in: cameras?.join(','),
        events__label__in: formData.eventLabel || null,
        timestamp__gte: Math.floor(startDate?.getTime() / 1000),
        timestamp__hour__gte__offset: startTime,
        timestamp__hour__lt__offset: endTime,
        confidence__events__gte: formData.confidence,
        event__metadata__type__in: eventRule,
      };
    }

    if (!this.formModel.noEnd) {
      params.timestamp__lte = Math.ceil(endDate?.getTime() / 1000);
    }

    switch (formData.label) {
      case '':
      case 'motion':
      case 'camera-motion':
        params.confidence__gte = 0;
        break;

      case 'car':
        params.metadata__car_color__in = formData.carColors;
        params.metadata__car_color__confidence__gte = formData.colorConfidence;
        params.metadata__vehicle_type__in = formData.vehicleType;
        if (formData.carColors) {
          params.metadata__car_color__in = formData.carColors;
        }

        if (formData.colorConfidence && params.metadata__car_color__in) {
          params.metadata__car_color__confidence__gte = formData.colorConfidence;
        }

        if (formData.make) {
          params.metadata__car_make__in = formData.make;
        }

        if (formData.vehicleType) {
          params.metadata__vehicle_type__in = formData.vehicleType;
        }
        break;
      case 'person':
        if (formData.is_male === '>=50') {
          params.metadata__attributes__is_male__confidence__gte = 50;
        } else if (formData.is_male === '< 50') {
          params.metadata__attributes__is_male__confidence__lte = 50;
        }
        if (formData.has_hat === '>=50') {
          params.metadata__attributes__has_hat__confidence__gte = 50;
        } else if (formData.has_hat === '< 50') {
          params.metadata__attributes__has_hat__confidence__lte = 50;
        }
        if (formData.has_backpack === '>=50') {
          params.metadata__attributes__has_backpack__confidence__gte = 50;
        } else if (formData.has_backpack === '< 50') {
          params.metadata__attributes__has_backpack__confidence__lte = 50;
        }
        if (formData.has_bag === '>=50') {
          params.metadata__attributes__has_bag__confidence__gte = 50;
        } else if (formData.has_bag === '< 50') {
          params.metadata__attributes__has_bag__confidence__lte = 50;
        }
        params.metadata__color_top__in = formData.colorTop;
        params.metadata__color_bottom__in = formData.colorBottom;
        break;
    }
    return params;
  }

  private _fetchFacets(params?: ISnListQueryParameters, onUpdate?: boolean): void {
    this.isLoading = true;
    this.isUpdatingFacets = onUpdate;
    this.progressMessage = t('common.loadingOptions');
    
    this._camerasAPI
      .getAiList({
        ...params,
        camera_id__in: this.formModel.aiCameras?.join(','),
        pageSize: 0,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res) => {
          if (res.facetFields.eventLabels) {
            if (this.eventLabels.length) {
              this.eventLabels = [];
            }
            res.facetFields.eventLabels.forEach((keys) => {
              this.eventLabels.push(keys);
            });
          }
          if (res.facetFields.detectionLabels) {
            if (this.detectionLabels.length) {
              this.detectionLabels = [];
            }
            res.facetFields.detectionLabels.forEach((keys) => {
              this.detectionLabels.push(keys);
            });
          }
          this._colors$.next(res.facetFields.carColors);
          this._vehicleTypes$.next(res.facetFields.vehicleTypes);
          this._detectionLabels$.next(res.facetFields.detectionLabels);
          this._eventLabels$.next(res.facetFields.eventLabels);
          this._eventRules$.next(res.facetFields.eventMetadataType);
          this._colorTop$.next(res.facetFields.colorTop);
          this._colorBottom$.next(res.facetFields.colorBottom);
          this._vehicleMakes$.next(res.facetFields.carMakes);

          if (res.facetFields.eventLabels.length === 0) {
            this.formFields[1].fieldGroup[0].templateOptions.disabled = true;
            this.formFields[1].fieldGroup[0].templateOptions.description = t('map.tools.sentrySearchForm.noEventsAvailable');
          }

          if (res.facetFields.eventLabels.length > 0) {
            this.formFields[1].fieldGroup[0].templateOptions.disabled = false;
            this.formFields[1].fieldGroup[0].templateOptions.description = '';
          }

          this._cd.detectChanges();
          this.isLoading = false;
          if (onUpdate) {
            this.isUpdatingFacets = false;
          }
        },
        error: (error) => {
          this.isLoading = false;
          if (onUpdate) {
            this.isUpdatingFacets = false;
          }
        },
      });
  }

  private _setForm(): void {
    this.formFields = [
      {
        key: 'aiCameras',
        type: 'select',
        templateOptions: {
          appearance: 'outline',
          floatLabel: 'always',
          placeholder: t('common.selectCameras'),
          required: true,
          label: t('common.cameras'),
          options: this.data,
          valueProp: 'id',
          labelProp: 'name',
          multiple: true,
          selectAllOption: t('common.selectAll'),
          clearable: true,
          change: () => {
            this._fetchFacets();
          },
        },
        hideExpression: () => !this.isNew,
      },
      {
        fieldGroup: [
          {
            key: 'eventDetection',
            type: 'radio',
            defaultValue: 'detection',
            templateOptions: {
              label: t('map.tools.createSentryDialog.form.eventOrDetection'),
              appearance: 'outline',
              inline: true,
              description: '',
              disabled: false,
              options: [
                { label: t('map.tools.createSentryDialog.form.detection'), value: 'detection' },
                { label: t('map.tools.createSentryDialog.form.event'), value: 'event' },
              ],
              change: (field, event) => {
                if (event.value === 'detection') {
                  field.parent.fieldGroup[1].hide = false;
                  field.parent.fieldGroup[2].hide = true;
                  field.parent.fieldGroup[2].formControl.setValue(null);
                  field.parent.fieldGroup[3].hide = true;
                }
                if (event.value === 'event') {
                  field.parent.fieldGroup[1].formControl.setValue(null);
                  field.parent.fieldGroup[1].hide = true;
                  field.parent.fieldGroup[2].hide = false;
                  field.parent.fieldGroup[3].hide = false;
                }
              },
            },
          },
          {
            key: 'label',
            type: 'select',
            hide: false,
            templateOptions: {
              appearance: 'outline',
              options: this.detectionLabels$,
              floatLabel: 'always',
              placeholder: t('map.tools.createSentryDialog.form.detectionTypePlaceholder'),
              label: t('map.tools.createSentryDialog.form.detection'),
              valueProp: 'key',
              labelProp: 'key',
              emptyLabel: t('common.any'),
              emptyOption: true,
              clearable: true,
              disabled: false,
            },
          },
          {
            key: 'eventLabel',
            type: 'select',
            hide: true,
            templateOptions: {
              appearance: 'outline',
              options: this.eventLabels$,
              floatLabel: 'always',
              placeholder: t('map.tools.createSentryDialog.form.eventDetectionPlaceholder'),
              label: t('map.tools.createSentryDialog.form.eventDetection'),
              valueProp: 'key',
              labelProp: 'key',
              emptyLabel: t('common.any'),
              emptyOption: true,
              clearable: true,
              disabled: false,
            },
          },
          {
            key: 'eventRule',
            type: 'select',
            hide: true,
            templateOptions: {
              appearance: 'outline',
              options: this.eventRules$,
              floatLabel: 'always',
              placeholder: t('map.tools.createSentryDialog.form.eventDetectionRulePlaceholder'),
              label: t('map.tools.createSentryDialog.form.eventDetectionRule'),
              valueProp: 'key',
              labelProp: 'key',
              emptyLabel: t('common.any'),
              emptyOption: true,
              clearable: true,
              disabled: false,
            },
          },
        ],
      },
      {
        key: 'colorTop',
        type: 'select',
        templateOptions: {
          appearance: 'outline',
          options: this.colorTop$,
          floatLabel: 'always',
          placeholder: t('common.selectTopColor'),
          label: t('common.topColor'),
          valueProp: 'key',
          labelProp: (v) => v.key.replace('pruple', 'purple'),
          emptyLabel: t('common.any'),
          emptyOption: true,
          clearable: true,
        },
        hideExpression: () => {
          return this.form.value.label !== 'person';
        },
      },
      {
        key: 'colorBottom',
        type: 'select',
        templateOptions: {
          appearance: 'outline',
          options: this.colorBottom$,
          floatLabel: 'always',
          placeholder: t('common.selectBottomColor'),
          label: t('common.bottomColor'),
          valueProp: 'key',
          labelProp: (v) => v.key.replace('pruple', 'purple'),
          emptyLabel: t('common.any'),
          emptyOption: true,
          clearable: true,
        },
        hideExpression: () => {
          return this.form.value.label !== 'person';
        },
      },
      {
        key: 'vehicleType',
        type: 'select',
        templateOptions: {
          appearance: 'outline',
          options: this.vehicleTypes$,
          floatLabel: 'always',
          placeholder: t('map.findVehicle.selectVehicleType'),
          label: t('common.vehicleType'),
          valueProp: 'key',
          labelProp: 'key',
          emptyOption: true,
          clearable: true,
        },
        hideExpression: () => {
          return this.form.value.label !== 'car' || !this.form.value.label;
        },
      },
      {
        key: 'make',
        type: 'select',
        templateOptions: {
          appearance: 'outline',
          options: this.vehicleMakes$,
          floatLabel: 'always',
          placeholder: t('map.findVehicle.selectVehicleMake'),
          label: t('common.vehicleMake'),
          valueProp: 'key',
          labelProp: 'key',
          emptyOption: true,
          clearable: true,
        },
        hideExpression: () => {
          return this.form.value.label !== 'car' || !this.form.value.label;
        },
      },
      {
        key: 'carColors',
        type: 'select',
        templateOptions: {
          appearance: 'outline',
          floatLabel: 'always',
          placeholder: t('common.selectColor'),
          infinityScroll: true,
          options: this.colors$,
          label: t('common.color'),
          labelProp: 'key',
          valueProp: 'key',
          emptyOption: true,
          clearable: true,
        },
        hideExpression: () => {
          return this.form.value.label !== 'car' || this.form.value.label === null;
        },
      },
      {
        key: 'confidence',
        type: 'slider',
        defaultValue: 75,
        templateOptions: {
          label: t('common.confidence'),
          min: 0,
          max: 100,
          thumbLabel: true,
          hideFieldUnderline: true,
        },
        hideExpression: () => {
          return (
            this.form.value.label === 'motion' ||
            this.form.value.label === '' ||
            this.form.value.label === 'camera-motion'
          );
        },
      },
      {
        key: 'colorConfidence',
        type: 'slider',
        defaultValue: 75,
        templateOptions: {
          label: t('map.tools.createSentryDialog.form.carColorConfidence'),
          min: 0,
          max: 100,
          thumbLabel: true,
          hideFieldUnderline: true,
        },
        hideExpression: () => {
          return this.form.value.label !== 'car' || !this.form.value.carColors;
        },
      },
      {
        key: 'is_male',
        type: 'select',
        templateOptions: {
          appearance: 'outline',
          floatLabel: 'always',
          placeholder: t('map.tools.createSentryDialog.form.genderPlaceholder'),
          label: t('common.gender'),
          options: [
            { label: t('common.either'), value: '' },
            { label: t('common.male'), value: '>=50' },
            { label: t('common.female'), value: '< 50' },
          ],
          clearable: true,
        },
        hideExpression: () => {
          return this.form.value.label !== 'person';
        },
      },
      {
        key: 'has_bag',
        type: 'select',
        templateOptions: {
          appearance: 'outline',
          floatLabel: 'always',
          placeholder: t('map.tools.createSentryDialog.form.hasBagPlaceholder'),
          options: [
            { label: t('common.either'), value: '' },
            { label: t('common.yes'), value: '>=50' },
            { label: t('common.no'), value: '< 50' },
          ],
          clearable: true,
        },
        hideExpression: () => {
          return this.form.value.label !== 'person';
        },
      },
      {
        key: 'has_backpack',
        type: 'select',
        templateOptions: {
          appearance: 'outline',
          floatLabel: 'always',
          placeholder: t('map.tools.createSentryDialog.form.hasBackpackPlaceholder'),
          options: [
            { label: t('common.either'), value: '' },
            { label: t('common.yes'), value: '>=50' },
            { label: t('common.no'), value: '< 50' },
          ],
          clearable: true,
        },
        hideExpression: () => {
          return this.form.value.label !== 'person';
        },
      },
      {
        key: 'has_hat',
        type: 'select',
        templateOptions: {
          appearance: 'outline',
          floatLabel: 'always',
          placeholder: t('map.tools.createSentryDialog.form.hasHatPlaceholder'),
          options: [
            { label: t('common.either'), value: '' },
            { label: t('common.yes'), value: '>=50' },
            { label: t('common.no'), value: '< 50' },
          ],
          clearable: true,
        },
        hideExpression: () => {
          return this.form.value.label !== 'person';
        },
      },
      {
        key: 'startDate',
        type: 'datetimepicker',
        templateOptions: {
          label: t('common.startDate'),
          appearance: 'fill',
          required: true,
          change: (field, value) => {
            this._updateFacetDates();
          },
          maxDate: () => this.formModel.endDate,
        },
      },
      {
        key: 'noEnd',
        type: 'checkbox',
        templateOptions: {
          label: t('common.forever'),
          appearance: 'fill',
          required: false,
          change: (field, event: MatCheckboxChange) => {
            this.formModel.endDate = !event.checked ? new Date() : null;
          },
        },
      },
      {
        key: 'endDate',
        type: 'datetimepicker',
        templateOptions: {
          label: t('common.endDate'),
          appearance: 'fill',
          required: true,
          change: (field, value) => {
            this._updateFacetDates();
          },
        },
        hideExpression: () => {
          return this.formModel.noEnd;
        },
      },
    ];
  }

  private _updateFacetDates(): void {
    this._fetchFacets(
      {
        timestamp__gte: Math.floor(this.formModel.startDate?.getTime() / 1000),
        timestamp__lte: Math.ceil(this.formModel.endDate?.getTime() / 1000),
      },
      true,
    );
  }
}
